import Cookies from "js-cookie";
import React, { FC, useEffect, useState } from "react";
import { Auth, Hub } from "aws-amplify";
import { Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Loader from "../components/elements/Loader";
import { ReduxApplicationState } from "../types/ReduxTypes/ReduxApplicationState";
import { SentryRoute } from "../sentry";
import { getAppHomePath } from "../utils/getAppHomePath";
import { setIsAuthLoading, setSession } from "../redux/reducers/session";

type AuthManagementRouteProps = {
  exact?: boolean;
  path: string;
  component: React.ComponentType<any>;
};

const applyAuthenticatedSession = async (dispatch: ReturnType<typeof useDispatch>) => {
  const user = await Auth.currentSession();
  if (!user) {
    return false;
  }

  const session = {
    accessToken: user.getAccessToken().getJwtToken(),
    idToken: user.getIdToken().getJwtToken(),
    refreshToken: user.getRefreshToken().getToken(),
  };
  dispatch(setSession(session));
  Cookies.set("ACCESS_TOKEN", session.accessToken);
  dispatch(setIsAuthLoading(false));
  return true;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const AuthManagementRoute: FC<AuthManagementRouteProps> = ({ component: Component, ...rest }) => {
  const isAuthenticated: boolean = useSelector((state: ReduxApplicationState) => state.session.isAuthenticated);
  const dispatch = useDispatch();

  const [isUserAuthenticatedChecking, setIsUserAuthenticatedChecking] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const hasOAuthCode = new URLSearchParams(window.location.search).has("code");

    const finishChecking = () => {
      if (isMounted) {
        setIsUserAuthenticatedChecking(false);
        dispatch(setIsAuthLoading(false));
      }
    };

    const hubListener = async ({ payload }: { payload: { event: string } }) => {
      if (payload.event === "signIn" || payload.event === "cognitoHostedUI") {
        try {
          await applyAuthenticatedSession(dispatch);
        } catch {
          // session will be retried below or the user stays on the auth page
        } finally {
          finishChecking();
        }
      }

      if (payload.event === "signIn_failure" || payload.event === "cognitoHostedUI_failure") {
        finishChecking();
      }
    };

    Hub.listen("auth", hubListener);

    const timeoutId = hasOAuthCode
      ? window.setTimeout(() => {
          finishChecking();
        }, 20000)
      : undefined;

    const checkAuthStatus = async () => {
      try {
        setIsUserAuthenticatedChecking(true);
        const applied = await applyAuthenticatedSession(dispatch);
        if (applied || !hasOAuthCode) {
          finishChecking();
        }
      } catch (error) {
        if (!hasOAuthCode) {
          if (error === "No current user") {
            // eslint-disable-next-line no-console
            console.log("User is not Authenticated!");
          } else {
            // eslint-disable-next-line no-console
            console.log("error:", error);
          }
          finishChecking();
        }
      }
    };
    checkAuthStatus();
    return () => {
      isMounted = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      Hub.remove("auth", hubListener);
    };
  }, [dispatch]);

  return (
    <SentryRoute
      render={(props) => (
        <>
          {isAuthenticated ? (
            <Redirect to={{ pathname: getAppHomePath() }} />
          ) : (
            <>{isUserAuthenticatedChecking ? <Loader /> : <Component {...props} />}</>
          )}
        </>
      )}
      {...rest}
    />
  );
};

export default AuthManagementRoute;
